/**
 * routes/Settings — the tunable-config editor (TF.6). Everything here is owner-tunable
 * and SAFETY-NEUTRAL (cadence, committee roles/models, token budgets, watch-list, news
 * sources, additive blackout windows) — it saves over `PUT /api/settings`, whose server
 * re-reads the file and echoes exactly what landed on disk (the round-trip contract).
 *
 * The rule engine's hard RISK LIMITS are shown strictly READ-ONLY: they live in an
 * owner-only, root-owned server config the dashboard has no path to edit, so this page
 * exposes no input for them and a PUT carrying a risk-shaped field is rejected 400.
 *
 * Layout is mobile-first: every section is a full-width Card whose rows stack on a phone
 * and align into a label/control grid on wider screens. A single sticky save bar reports
 * validation, the in-flight save and the persisted round-trip via an aria-live region.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiError, UnauthorizedError } from '../api/client';
import type { CommitteeRole, CustomBlackout, TunableSettings } from '../api/types';
import { ALLOWED_INTERVALS, ALLOWED_MODELS, BLACKOUT_TYPES, COMMITTEE_ROLES, ROLE_LABEL } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { Card } from '../components/Card';

// ── editable draft (strings for numeric fields so inputs clear cleanly) ──────────

interface DraftRole {
  enabled: boolean;
  model: string;
  cap: string;
}
interface Draft {
  symbolsText: string;
  cadence: Record<string, string>;
  roles: Record<CommitteeRole, DraftRole>;
  dailyTokenBudget: string;
  cryptoPanicEnabled: boolean;
  rssText: string;
  blackout: CustomBlackout[];
}

const DEFAULT_INTERVAL = '15m';

/** Parse a free-text watch-list into a deduped, uppercased symbol list. */
function parseSymbols(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of text.toUpperCase().split(/[^A-Z0-9]+/)) {
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
}

/** Server settings → editable draft. */
function toDraft(s: TunableSettings): Draft {
  const roles = {} as Record<CommitteeRole, DraftRole>;
  for (const r of COMMITTEE_ROLES) {
    const rs = s.roles[r] ?? { enabled: true, model: 'sonnet' };
    roles[r] = { enabled: rs.enabled, model: rs.model, cap: String(s.budget.perRoleTokenCap[r] ?? '') };
  }
  return {
    symbolsText: s.symbols.join(' '),
    cadence: { ...s.cadence },
    roles,
    dailyTokenBudget: String(s.budget.dailyTokenBudget),
    cryptoPanicEnabled: s.news.cryptoPanicEnabled,
    rssText: s.news.rssFeeds.join('\n'),
    blackout: s.blackout.customEvents.map((e) => ({ ...e })),
  };
}

function intOrNaN(v: string): number {
  return /^-?\d+$/.test(v.trim()) ? Number.parseInt(v.trim(), 10) : NaN;
}

/** Build the PUT payload from the draft (the canonical shape the server validates). */
function toPayload(d: Draft): TunableSettings {
  const symbols = parseSymbols(d.symbolsText);
  const cadence: Record<string, string> = {};
  for (const sym of symbols) cadence[sym] = d.cadence[sym] ?? DEFAULT_INTERVAL;

  const roles = {} as Record<string, { enabled: boolean; model: string }>;
  const perRoleTokenCap: Record<string, number> = {};
  for (const r of COMMITTEE_ROLES) {
    // The Arbiter synthesizes every other role — it can never be turned off.
    roles[r] = { enabled: r === 'arbiter' ? true : d.roles[r].enabled, model: d.roles[r].model };
    perRoleTokenCap[r] = intOrNaN(d.roles[r].cap);
  }

  const rssFeeds = d.rssText.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  const customEvents = d.blackout
    .map((e) => ({ type: e.type, label: e.label.trim(), time: e.time.trim() }))
    .filter((e) => e.label !== '' || e.time !== '');

  return {
    symbols,
    cadence,
    roles,
    budget: { dailyTokenBudget: intOrNaN(d.dailyTokenBudget), perRoleTokenCap },
    news: { cryptoPanicEnabled: d.cryptoPanicEnabled, rssFeeds },
    blackout: { customEvents },
  };
}

/** Client-side validation for immediate feedback; the server stays authoritative. */
function validate(p: TunableSettings): string | null {
  if (p.symbols.length === 0) return 'Add at least one symbol to the watch-list.';
  for (const s of p.symbols) {
    if (!/^[A-Z0-9]{4,20}$/.test(s)) return `"${s}" is not a valid symbol (4–20 uppercase letters/digits, e.g. BTCUSDT).`;
  }
  if (!Number.isInteger(p.budget.dailyTokenBudget) || p.budget.dailyTokenBudget < 1) {
    return 'Daily token budget must be a whole number ≥ 1.';
  }
  for (const r of COMMITTEE_ROLES) {
    const cap = p.budget.perRoleTokenCap[r];
    if (cap === undefined || !Number.isInteger(cap) || cap < 1) return `${ROLE_LABEL[r]} token cap must be a whole number ≥ 1.`;
  }
  for (const u of p.news.rssFeeds) {
    if (!/^https?:\/\/\S+$/.test(u)) return `RSS feed "${u}" must be an http(s) URL.`;
  }
  for (const [i, e] of p.blackout.customEvents.entries()) {
    if (!BLACKOUT_TYPES.includes(e.type as (typeof BLACKOUT_TYPES)[number])) {
      return `Blackout #${i + 1}: type must be one of ${BLACKOUT_TYPES.join(', ')}.`;
    }
    if (e.label === '') return `Blackout #${i + 1}: add a label.`;
    if (Number.isNaN(Date.parse(e.time))) return `Blackout #${i + 1}: time must be an ISO-8601 timestamp (e.g. 2026-08-13T12:30:00Z).`;
  }
  return null;
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'invalid'; message: string }
  | { kind: 'error'; message: string };

export function Settings(): JSX.Element {
  const { logout } = useAuth();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [riskLimits, setRiskLimits] = useState<Readonly<Record<string, string>>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setDraft(null);
    setLoadError(null);
    api
      .settings(controller.signal)
      .then((res) => {
        if (cancelled) return;
        setDraft(toDraft(res.settings));
        setRiskLimits(res.riskLimits ?? {});
        setDirty(false);
        setSave({ kind: 'idle' });
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        if (err instanceof UnauthorizedError) return void logout();
        setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [reloadNonce, logout]);

  // A typed helper that mutates the draft immutably and marks the form dirty.
  const patch = useCallback((fn: (d: Draft) => Draft) => {
    setDraft((d) => (d ? fn(d) : d));
    setDirty(true);
    setSave((s) => (s.kind === 'saving' ? s : { kind: 'idle' }));
  }, []);

  const symbols = useMemo(() => (draft ? parseSymbols(draft.symbolsText) : []), [draft?.symbolsText]);

  const onSave = useCallback(async () => {
    if (!draft) return;
    const payload = toPayload(draft);
    const invalid = validate(payload);
    if (invalid) {
      setSave({ kind: 'invalid', message: invalid });
      return;
    }
    setSave({ kind: 'saving' });
    try {
      const res = await api.saveSettings(payload);
      setDraft(toDraft(res.settings));
      setDirty(false);
      setSave({ kind: 'saved' });
    } catch (err) {
      if (err instanceof UnauthorizedError) return void logout();
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      setSave({ kind: 'error', message });
    }
  }, [draft, logout]);

  if (loadError) {
    return (
      <>
        <PageHead />
        <div className="banner-error section" role="alert">
          Settings unavailable: {loadError}
        </div>
        <button type="button" className="btn" onClick={() => setReloadNonce((n) => n + 1)}>
          Retry
        </button>
      </>
    );
  }

  if (!draft) {
    return (
      <>
        <PageHead />
        <div className="state"><span className="spinner" aria-hidden /><span>Loading settings…</span></div>
      </>
    );
  }

  return (
    <>
      <PageHead dirty={dirty} />

      {/* ── Symbols & cadence ────────────────────────────────────────────── */}
      <Card title="Symbols & cadence" hint={`${symbols.length} watched`}>
        <div className="set-field">
          <label className="set-field__label" htmlFor="set-symbols">Watch-list</label>
          <div className="set-field__control">
            <input
              id="set-symbols"
              className="input set-input--wide"
              value={draft.symbolsText}
              placeholder="BTCUSDT ETHUSDT SOLUSDT"
              autoCapitalize="characters"
              spellCheck={false}
              onChange={(e) => patch((d) => ({ ...d, symbolsText: e.target.value }))}
            />
            <p className="set-hint">Space- or comma-separated. New symbols default to {DEFAULT_INTERVAL} cadence.</p>
          </div>
        </div>

        {symbols.length > 0 && (
          <div className="set-grid set-grid--cadence" role="group" aria-label="Per-symbol cadence">
            {symbols.map((sym) => (
              <div className="set-cadence" key={sym}>
                <label className="set-cadence__sym" htmlFor={`cad-${sym}`}>{sym}</label>
                <select
                  id={`cad-${sym}`}
                  className="input set-select"
                  value={draft.cadence[sym] ?? DEFAULT_INTERVAL}
                  onChange={(e) => patch((d) => ({ ...d, cadence: { ...d.cadence, [sym]: e.target.value } }))}
                >
                  {ALLOWED_INTERVALS.map((iv) => <option key={iv} value={iv}>{iv}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Committee roles & model tiers ────────────────────────────────── */}
      <Card title="Committee roles & models" hint="on/off · tier · per-wake cap">
        <div className="role-table" role="table" aria-label="Committee roles">
          <div className="role-row role-row--head" role="row">
            <span role="columnheader">Role</span>
            <span role="columnheader">Enabled</span>
            <span role="columnheader">Model tier</span>
            <span role="columnheader">Token cap</span>
          </div>
          {COMMITTEE_ROLES.map((r) => {
            const isArbiter = r === 'arbiter';
            const rs = draft.roles[r];
            return (
              <div className="role-row" role="row" key={r}>
                <span className="role-row__name" role="cell">{ROLE_LABEL[r]}</span>
                <span role="cell">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isArbiter ? true : rs.enabled}
                      disabled={isArbiter}
                      aria-label={`${ROLE_LABEL[r]} enabled`}
                      title={isArbiter ? 'The Arbiter synthesizes every other role and cannot be disabled.' : undefined}
                      onChange={(e) => patch((d) => ({ ...d, roles: { ...d.roles, [r]: { ...d.roles[r], enabled: e.target.checked } } }))}
                    />
                    <span className="switch__text">{isArbiter ? 'always on' : rs.enabled ? 'on' : 'off'}</span>
                  </label>
                </span>
                <span role="cell">
                  <select
                    className="input set-select"
                    value={rs.model}
                    aria-label={`${ROLE_LABEL[r]} model tier`}
                    onChange={(e) => patch((d) => ({ ...d, roles: { ...d.roles, [r]: { ...d.roles[r], model: e.target.value } } }))}
                  >
                    {ALLOWED_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </span>
                <span role="cell">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1000}
                    className="input set-num"
                    value={rs.cap}
                    aria-label={`${ROLE_LABEL[r]} per-wake token cap`}
                    onChange={(e) => patch((d) => ({ ...d, roles: { ...d.roles, [r]: { ...d.roles[r], cap: e.target.value } } }))}
                  />
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* ── Token budget ─────────────────────────────────────────────────── */}
      <Card title="Token budget">
        <div className="set-field">
          <label className="set-field__label" htmlFor="set-daily">Daily budget</label>
          <div className="set-field__control">
            <input
              id="set-daily"
              type="number"
              inputMode="numeric"
              min={1}
              step={10000}
              className="input set-num set-num--wide"
              value={draft.dailyTokenBudget}
              onChange={(e) => patch((d) => ({ ...d, dailyTokenBudget: e.target.value }))}
            />
            <p className="set-hint">Billable tokens per UTC day. Once hit, the committee stops waking → NO&nbsp;TRADE.</p>
          </div>
        </div>
      </Card>

      {/* ── News sources ─────────────────────────────────────────────────── */}
      <Card title="News sources">
        <div className="set-field">
          <span className="set-field__label">CryptoPanic</span>
          <div className="set-field__control">
            <label className="switch">
              <input
                type="checkbox"
                checked={draft.cryptoPanicEnabled}
                onChange={(e) => patch((d) => ({ ...d, cryptoPanicEnabled: e.target.checked }))}
              />
              <span className="switch__text">{draft.cryptoPanicEnabled ? 'enabled' : 'disabled'}</span>
            </label>
          </div>
        </div>
        <div className="set-field set-field--top">
          <label className="set-field__label" htmlFor="set-rss">RSS / Atom feeds</label>
          <div className="set-field__control">
            <textarea
              id="set-rss"
              className="input set-textarea"
              rows={4}
              spellCheck={false}
              placeholder="one URL per line"
              value={draft.rssText}
              onChange={(e) => patch((d) => ({ ...d, rssText: e.target.value }))}
            />
            <p className="set-hint">One http(s) URL per line.</p>
          </div>
        </div>
      </Card>

      {/* ── Blackout calendar (additive only) ────────────────────────────── */}
      <Card
        title="Blackout calendar"
        hint="additive only"
        actions={
          <button
            type="button"
            className="btn btn--ghost set-add"
            onClick={() => patch((d) => ({ ...d, blackout: [...d.blackout, { type: BLACKOUT_TYPES[0], label: '', time: '' }] }))}
          >
            + Add window
          </button>
        }
      >
        <p className="set-hint set-hint--block">
          Extra caution windows layered on top of the committed FOMC/CPI/NFP schedule. These can only <strong>ADD</strong>
          {' '}blackouts, never remove a committed one.
        </p>
        {draft.blackout.length === 0 ? (
          <p className="secondary" style={{ margin: 0 }}>No additional blackout windows.</p>
        ) : (
          <div className="blackout-list">
            {draft.blackout.map((ev, i) => (
              <div className="blackout-row" key={i}>
                <select
                  className="input set-select"
                  value={ev.type}
                  aria-label={`Blackout ${i + 1} type`}
                  onChange={(e) => patch((d) => ({ ...d, blackout: d.blackout.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)) }))}
                >
                  {BLACKOUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input
                  className="input"
                  placeholder="label (e.g. US CPI June)"
                  value={ev.label}
                  aria-label={`Blackout ${i + 1} label`}
                  onChange={(e) => patch((d) => ({ ...d, blackout: d.blackout.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) }))}
                />
                <input
                  className="input set-mono"
                  placeholder="2026-08-13T12:30:00Z"
                  value={ev.time}
                  aria-label={`Blackout ${i + 1} time (ISO-8601 UTC)`}
                  onChange={(e) => patch((d) => ({ ...d, blackout: d.blackout.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)) }))}
                />
                <button
                  type="button"
                  className="btn btn--ghost blackout-row__rm"
                  aria-label={`Remove blackout window ${i + 1}`}
                  onClick={() => patch((d) => ({ ...d, blackout: d.blackout.filter((_, j) => j !== i) }))}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Risk limits (READ-ONLY) ──────────────────────────────────────── */}
      <Card title={<>Risk limits <span className="ro-tag">read-only</span></>}>
        <p className="set-hint set-hint--block">
          The rule engine's hard safety limits. They live in an owner-only, root-owned server config — the dashboard has
          no path to edit them, by design. The owner changes them via server config.
        </p>
        {Object.keys(riskLimits).length === 0 ? (
          <p className="secondary" style={{ margin: 0 }}>No risk limits reported.</p>
        ) : (
          <dl className="risk-dl">
            {Object.entries(riskLimits).map(([k, v]) => (
              <div className="risk-dl__row" key={k}>
                <dt>{k}</dt>
                <dd className="num">{v}</dd>
              </div>
            ))}
          </dl>
        )}
      </Card>

      {/* ── Sticky save bar ──────────────────────────────────────────────── */}
      <div className="save-bar">
        <div className="save-bar__msg" role="status" aria-live="polite">
          {save.kind === 'saving' && <span className="secondary">Saving…</span>}
          {save.kind === 'saved' && <span className="save-bar__ok">✓ Saved — persisted and re-read from disk.</span>}
          {save.kind === 'invalid' && <span className="save-bar__bad">{save.message}</span>}
          {save.kind === 'error' && <span className="save-bar__bad">Rejected: {save.message}</span>}
          {save.kind === 'idle' && (dirty ? <span className="secondary">Unsaved changes.</span> : <span className="muted">Changes apply on the next committee wake.</span>)}
        </div>
        <button
          type="button"
          className="btn btn--primary"
          disabled={save.kind === 'saving' || !dirty}
          onClick={() => void onSave()}
        >
          {save.kind === 'saving' ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </>
  );
}

function PageHead({ dirty }: { readonly dirty?: boolean }): JSX.Element {
  return (
    <div className="page-head">
      <h1 className="page-head__title">Settings</h1>
      <span className="page-head__sub">{dirty ? 'unsaved changes' : 'tunable committee & pre-filter config'}</span>
    </div>
  );
}
